import React from 'react';
import { BookOpen, Lightbulb, Headphones, ExternalLink } from 'lucide-react';

import { useTranslation } from 'react-i18next';

interface ChatInformationCardProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  link?: string;
  onClick?: () => void;
}

const ChatInformationCard: React.FC<ChatInformationCardProps> = ({
  icon,
  title,
  description,
  link,
  onClick,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      onClick={handleClick}
      className="group relative cursor-pointer rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md active:scale-[0.98] dark:border-gray-700 dark:from-gray-900 dark:to-gray-800 dark:hover:border-gray-600"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#FF732E]/10 text-[#FF732E] transition-colors group-hover:bg-[#FF732E]/20">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h3>
            {(link || onClick) && (
              <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-colors group-hover:text-[#FF732E] dark:text-gray-500" />
            )}
          </div>
          {description && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export const DocsChatInformationCard: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  return (
    <ChatInformationCard
      icon={<BookOpen className="h-5 w-5" />}
      title={t('accessPleriDocumentation')}
      description={t('exploreOurComprehensiveDocumentationToLearnMore')}
      link="https://docs.pleri.ai"
    />
  );
};

export const FeatureRequestChatInformationCard: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  return (
    <ChatInformationCard
      icon={<Lightbulb className="h-5 w-5" />}
      title={t('submitAFeatureRequest')}
      description={t('shareYourIdeasAndHelpUsImprove')}
    />
  );
};

export const ContactSupportChatInformationCard: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  return (
    <ChatInformationCard
      icon={<Headphones className="h-5 w-5" />}
      title={t('contactSupport')}
      description={t('getHelpWithAnyIssuesOrQuestions')}
    />
  );
};
